'use no memo';

import {isEqual} from 'date-fns';
import {
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  openDialog,
} from 'platform/components';
import {Box, Hide, HStack, Right, Show, Space, Stack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {object} from 'yup';

import {RefObject} from 'react';
import {useSelector} from 'react-redux';

import {isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchQuery} from '@dms/api/core';
import {useGetEmployeeWorkCompetencesListQuery} from '@dms/api/customer';
import {selectActiveBranchId} from '@dms/api/features';
import {getOptionsFromServiceCaseOrderVariants} from '@dms/api/utils';
import {useGetServiceCaseOrderVariantsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {parseDate, precisionCalculation, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {WORK_TIME_DECIMAL_PLACES} from '../../constants/WORK_TIME_DECIMAL_PLACES';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {getActiveOptions} from '../../utils/getActiveOptions';
import {NoPermissionTooltip} from '../NoPermissionTooltip/NoPermissionTooltip';
import {PredefinedNotes} from '../PredefinedNotes/PredefinedNotes';
import {NeededAtConfirmationDialog} from './components/NeededAtConfirmationDialog';
import {JobType} from './types/JobType';
import {NeededAtDecision} from './types/NeededAtDecision';

type JobTypeForm = JobType & {
  neededAt: Date | null;
};

export type JobFormRef = {
  submit: () => Promise<boolean>;
  isDirty: boolean;
};

export interface JobFormProps extends TestIdProps {
  serviceCaseId?: string;
  jobData?: JobType | null;
  orderId?: string;
  orderVariantId?: string;
  isReadOnly?: boolean;
  isLockedServicePackage?: boolean;
  onSubmit: (job: JobType, reset: () => void) => Promise<void>;
  formRef?: RefObject<JobFormRef | null>;
}

export function JobForm(props: JobFormProps) {
  const [canEditPartsListReady, canEditPartsListRequest, canEditSolution, canReadEmployee] =
    usePermissions({
      permissionKeys: [
        'editPartsListReady',
        'editPartsListRequest',
        'editSolution',
        'employeeRead',
      ],
    });

  const branchId = useSelector(selectActiveBranchId);
  const {data: branchDetail} = useGetBranchQuery({branchId});
  const {data: orderVariants, isLoading: isOrderVariantsLoading} =
    useGetServiceCaseOrderVariantsQuery(
      {serviceCaseId: props.serviceCaseId || ''},
      {skip: !isNil(props.orderId) || isNil(props.serviceCaseId)}
    );
  const {competenceOptions, isCompetenceOptionsLoading} = useGetEmployeeWorkCompetencesListQuery(
    undefined,
    {
      selectFromResult: ({data, isLoading}) => ({
        isCompetenceOptionsLoading: isLoading,
        competenceOptions: getActiveOptions(data?.competences),
      }),
      skip:
        isNilOrEmpty(branchDetail?.billingInformation?.id) ||
        isNilOrEmpty(branchDetail?.id) ||
        !canReadEmployee,
    }
  );

  const defaultValues = props.jobData
    ? {
        ...props.jobData,
        orderVariantId: props.jobData.orderVariantId ?? props.orderVariantId,
        workTimeEstimated: isNotNil(props.jobData.workTimeEstimated)
          ? precisionCalculation.round(props.jobData.workTimeEstimated, WORK_TIME_DECIMAL_PLACES)
          : null,
        workTimeReal: isNotNil(props.jobData.workTimeReal)
          ? precisionCalculation.round(props.jobData.workTimeReal, WORK_TIME_DECIMAL_PLACES)
          : null,
        neededAt: isNotNil(props.jobData.neededAt)
          ? (parseDate(props.jobData.neededAt) as Date & string)
          : null,
      }
    : undefined;

  const onSubmit: FormSubmitHandler<JobTypeForm> = async (job, _, resetFunc, blockerContext) => {
    const workTimeEstimated = job.workTimeEstimated ?? null;

    const workTimeReal = job.workTimeReal ?? null;

    const reset = () => resetFunc(undefined, {keepValues: true});

    const jobNeededAt = parseDate(props.jobData?.neededAt || 0);
    const formNeededAt = parseDate(job.neededAt || 0);

    const data = {...job, workTimeEstimated, workTimeReal};

    const hasNeededAtChanged = not(isEqual(jobNeededAt, formNeededAt));

    if (not(hasNeededAtChanged) || not(props.jobData?.hasItems)) {
      await props.onSubmit(data, reset);
      return;
    }

    const withItemNeededAtUpdate = match(await requestNeededAtDecision())
      .with('update', () => true)
      .with('dontUpdate', () => false)
      .with('cancel', () => null)
      .exhaustive();

    if (isNil(withItemNeededAtUpdate)) {
      // The blocker navigates away as soon as this handler resolves - reporting a failed submit
      // keeps the unsaved change on screen instead of silently discarding it.
      blockerContext?.reportApiFailure?.();
      return;
    }

    await props.onSubmit({...data, withItemNeededAtUpdate}, reset);
  };

  const requestNeededAtDecision = () =>
    new Promise<NeededAtDecision>((resolve) => {
      openDialog(
        <NeededAtConfirmationDialog
          onDecision={resolve}
          data-testid={suffixTestId('neededAtConfirmationDialog', props)}
        />,
        {
          id: 'neededAtUpdateConfirmDialog',
          size: 'small',
          onCloseComplete: () => resolve('cancel'),
          'data-testid': suffixTestId('neededAtUpdateConfirmDialog', props),
        }
      );
    });

  return (
    <Form<JobTypeForm>
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      schema={FormSchema}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => {
        if (props.formRef) {
          props.formRef.current = {
            submit: () => control.submitForm(),
            isDirty: formApi.formState.isDirty,
          };
        }

        return (
          <>
            <HStack spacing={4}>
              <Box flex={1}>
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  label={i18n.t('entity.orderRequest.labels.request')}
                  isRequired
                  isDisabled={props.isReadOnly || props.isLockedServicePackage}
                  data-testid={suffixTestId('name', props)}
                />
              </Box>
              <Hide when={props.orderId || !props.serviceCaseId}>
                <Box flex={1}>
                  <FormField
                    control={control}
                    label={i18n.t('entity.orderRequest.labels.orderVariant')}
                    name="orderVariantId"
                    type="choice"
                    isRequired
                    options={getOptionsFromServiceCaseOrderVariants(orderVariants)}
                    placeholder={i18n.t('general.labels.select')}
                    isDisabled={props.isReadOnly}
                    isLoading={isOrderVariantsLoading}
                    data-testid={suffixTestId('orderVariantId', props)}
                  />
                </Box>
              </Hide>
              <Show when={props.orderId || !props.serviceCaseId}>
                <Box flex={1}>
                  <FormField
                    control={control}
                    label={i18n.t('entity.warehouse.labels.neededAt')}
                    name="neededAt"
                    type="date"
                    isDisabled={props.isReadOnly}
                    data-testid={suffixTestId('neededAt', props)}
                  />
                </Box>
              </Show>
            </HStack>
            <Space vertical={4} />
            <HStack spacing={4}>
              <Box flex={1}>
                <HStack justify="space-between" align="flex-end">
                  <Box flex={1}>
                    <Label>{i18n.t('entity.orderRequest.labels.description')}</Label>
                  </Box>
                  <PredefinedNotes
                    note={formApi.watch('description') ?? null}
                    onPrefill={(note) => formApi.setValue('description', note)}
                    resource="SERVICE_CASE"
                    context="service_request_description"
                    isLinkVariant
                    data-testid={suffixTestId('descriptionPredefinedNotes', props)}
                  />
                </HStack>
                <FormField
                  control={control}
                  name="description"
                  type="textarea"
                  isDisabled={props.isReadOnly}
                  data-testid={suffixTestId('description', props)}
                />
              </Box>
              <Box flex={1}>
                <HStack justify="space-between" align="flex-end">
                  <Box flex={1}>
                    <Label>{i18n.t('entity.orderRequest.labels.solution')}</Label>
                  </Box>
                  <Show when={canEditSolution}>
                    <PredefinedNotes
                      note={formApi.watch('solution') ?? null}
                      onPrefill={(note) => formApi.setValue('solution', note)}
                      resource="SERVICE_CASE"
                      context="service_request_solution"
                      isLinkVariant
                      data-testid={suffixTestId('solutionPredefinedNotes', props)}
                    />
                  </Show>
                </HStack>
                <NoPermissionTooltip shouldShowTooltip={!canEditSolution}>
                  <FormField
                    control={control}
                    name="solution"
                    type="textarea"
                    isDisabled={props.isReadOnly || !canEditSolution}
                    data-testid={suffixTestId('solution', props)}
                  />
                </NoPermissionTooltip>
              </Box>
            </HStack>
            <Space vertical={4} />
            <Stack direction={['column', 'column', 'column', 'row']} spacing={4}>
              <Box flex={1}>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="competencyId"
                      type="choice"
                      label={i18n.t('entity.orderRequest.labels.competence')}
                      isDisabled={props.isReadOnly || props.isLockedServicePackage}
                      options={competenceOptions}
                      isLoading={isCompetenceOptionsLoading}
                      data-testid={suffixTestId('competency', props)}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="workTimeEstimated"
                      type="number"
                      label={i18n.t('entity.orderRequest.labels.estimatedTime')}
                      decimalPlaces={WORK_TIME_DECIMAL_PLACES}
                      suffix={i18n.t('general.labels.hour')}
                      isDisabled={props.isReadOnly || props.isLockedServicePackage}
                      data-testid={suffixTestId('workTimeEstimated', props)}
                    />
                  </Box>{' '}
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="workTimeReal"
                      type="number"
                      label={i18n.t('entity.orderRequest.labels.realTime')}
                      decimalPlaces={WORK_TIME_DECIMAL_PLACES}
                      suffix={i18n.t('general.labels.hour')}
                      isDisabled={props.isReadOnly || props.isLockedServicePackage}
                      data-testid={suffixTestId('workTimeReal', props)}
                    />
                  </Box>
                </HStack>
              </Box>
              <Box flex={1}>
                <Label>{i18n.t('entity.workshop.labels.additionalOptions')}</Label>
                <HStack spacing={4} height={8} align="center">
                  <FormField
                    control={control}
                    name="repeatedRepair"
                    type="checkbox"
                    isDisabled={props.isReadOnly}
                    label={i18n.t('entity.workshop.labels.repeatedRepair')}
                    data-testid={suffixTestId('repeatedRepair', props)}
                  />
                  <NoPermissionTooltip shouldShowTooltip={!canEditPartsListRequest}>
                    <FormField
                      control={control}
                      name="partsRequest"
                      type="checkbox"
                      isDisabled={
                        props.isReadOnly ||
                        !!formApi.watch('partsReady') ||
                        !canEditPartsListRequest
                      }
                      label={i18n.t('entity.workshop.labels.partsRequest')}
                      data-testid={suffixTestId('partsRequest', props)}
                    />
                  </NoPermissionTooltip>
                  <NoPermissionTooltip shouldShowTooltip={!canEditPartsListReady}>
                    <FormField
                      control={control}
                      name="partsReady"
                      type="checkbox"
                      isDisabled={
                        props.isReadOnly || !formApi.watch('partsRequest') || !canEditPartsListReady
                      }
                      label={i18n.t('entity.workshop.labels.partsReady')}
                      data-testid={suffixTestId('partsReady', props)}
                    />
                  </NoPermissionTooltip>
                </HStack>
              </Box>
            </Stack>
            <Hide when={props.isReadOnly}>
              <Space vertical={4} />
              <Right>
                <FormButton
                  control={control}
                  type="submit"
                  data-testid={suffixTestId('submit', props)}
                  isLoading={formApi.formState.isSubmitting}
                  title={i18n.t('general.actions.saveChanges')}
                />
              </Right>
            </Hide>
          </>
        );
      }}
    </Form>
  );
}

const FormSchema = object({
  name: yupString.required(),
  orderVariantId: yupString.required(),
});
